import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, from, of, switchMap, tap, throwError } from 'rxjs';
import { AuthStateService } from '../auth/auth-state.service';
import { ApiErrorService } from '../services/api-error.service';
import { AppHttpError, sanitizeApiDetail } from '../http/app-http-error';
import { classifyApiFailure } from '../http/api-failure-policy';
import { FeatureFlagsService } from '../services/feature-flags.service';

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';
/** Survives login ngOnInit clearing — login reads and removes this once. */
const SESSION_NOTICE_KEY = 'ct_session_notice';

/** Login / MFA / guest-invite / logout keep raw HttpErrorResponse for their own handlers.
 *  Admin collection routes (/auth/users, /auth/invites) use the shared shell banner. */
function isAuthFlowEndpoint(url: string): boolean {
  const path = url.split('?')[0];
  if (
    path.includes('/auth/login') ||
    path.includes('/auth/otp') ||
    path.includes('/auth/status') ||
    path.includes('/auth/methods') ||
    path.includes('/auth/microsoft') ||
    path.includes('/auth/logout')
  ) {
    return true;
  }
  return /\/auth\/invites\/[^/]+/.test(path);
}

function detailFromBody(error: HttpErrorResponse, fallback: string): string {
  return sanitizeApiDetail(error.error?.detail, fallback);
}

function isBannerNoiseEndpoint(url: string): boolean {
  const path = url.split('?')[0].toLowerCase();
  return path.endsWith('/health') || path.endsWith('/api/health');
}

/** Blob downloads return JSON errors as Blob — unwrap `detail` for 402 etc. */
function normalizeErrorBody(error: HttpErrorResponse): Observable<HttpErrorResponse> {
  if (!(error.error instanceof Blob)) {
    return of(error);
  }
  return from(error.error.text()).pipe(
    switchMap((text) => {
      let body: unknown = { detail: text };
      try {
        body = JSON.parse(text);
      } catch {
        /* keep plain text */
      }
      return of(
        new HttpErrorResponse({
          error: body,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined
        })
      );
    })
  );
}

/**
 * Global HTTP error handling:
 * - 401 → session clear + login
 * - 402 → backend detail + Billing link when showBilling is on
 * - 5xx / network → banner + Try Again capture
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authState = inject(AuthStateService);
  const apiError = inject(ApiErrorService);
  const features = inject(FeatureFlagsService);

  if (isAuthFlowEndpoint(req.url)) {
    return next(req).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  return next(req).pipe(
    tap({
      next: () => apiError.noteSuccess(req.urlWithParams || req.url, req.method)
    }),
    catchError((raw: HttpErrorResponse) =>
      normalizeErrorBody(raw).pipe(
        switchMap((error) => {
          let message = 'An unexpected network error occurred.';
          let setGlobalBanner = false;

          switch (error.status) {
            case 0:
              message =
                'Cannot reach the OneDesk API. Check that the backend is running and try again.';
              setGlobalBanner = true;
              break;

            case 401: {
              const sessionMessage = detailFromBody(error, SESSION_EXPIRED_MESSAGE);
              authState.clear();
              apiError.clear();
              try {
                sessionStorage.setItem(SESSION_NOTICE_KEY, sessionMessage);
              } catch {
                /* private mode / blocked storage */
              }
              authState.setError(sessionMessage);
              if (!router.url.startsWith('/login')) {
                void router.navigate(['/login'], {
                  queryParams: { reason: 'session_expired' },
                  replaceUrl: true
                });
              }
              return throwError(() => new AppHttpError(sessionMessage, 401, error));
            }

            case 403:
              message = detailFromBody(
                error,
                'Access denied. You do not have permission to perform this action.'
              );
              break;

            case 402: {
              message = detailFromBody(
                error,
                'An active OneDesk subscription is required. Open Billing to subscribe.'
              );
              if (features.billingEnabled()) {
                apiError.setPaymentRequired(message);
              } else {
                apiError.set(message);
              }
              return throwError(() => new AppHttpError(message, 402, error));
            }

            case 404:
              message = detailFromBody(error, 'Requested resource not found.');
              break;

            case 409:
              message = detailFromBody(error, 'Conflict error. Item already exists.');
              break;

            case 422:
              message = detailFromBody(error, 'Invalid submission format.');
              break;

            case 429:
              message = 'Too many requests. Please wait a moment and try again.';
              break;

            case 500:
              message = detailFromBody(error, 'Server error. Please try again.');
              setGlobalBanner = true;
              break;

            case 503:
              message = detailFromBody(
                error,
                'A required service is unavailable. Please try again later.'
              );
              setGlobalBanner = true;
              break;

            default:
              message = detailFromBody(error, 'An unexpected network error occurred.');
              if (error.status >= 500) {
                setGlobalBanner = true;
              }
              break;
          }

          if (setGlobalBanner) {
            const requestUrl = req.urlWithParams || req.url;
            const classified = classifyApiFailure(req.method, requestUrl);
            if (classified?.retryable) {
              apiError.capture({
                key: classified.key,
                purpose: classified.purpose,
                method: req.method.toUpperCase(),
                url: requestUrl,
                message,
                status: error.status,
                capturedAt: Date.now()
              });
            } else if (!isBannerNoiseEndpoint(requestUrl)) {
              apiError.set(message);
            }
          }

          return throwError(() => new AppHttpError(message, error.status, error));
        })
      )
    )
  );
};
